import { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader, Megaphone } from 'lucide-react';
import { modpackConfigsApiService } from '../../services/modpackConfigsApi';
import { AnnounceLaunchResult, ModpackConfigEnv } from '../../types/modpackConfig';
import { SectionHeader } from '../ui/SectionHeader';
import { cn } from '../../utils/cn';

/** A server the announcement can name: the address, and the region it starts out labelled with. */
export interface LaunchServer {
  server_address: string;
  region_name: string | null;
}

interface AnnounceLaunchPanelProps {
  address: string;
  env: ModpackConfigEnv;
  /** Pre-filled from the config where known. */
  defaults?: { location?: string | null; version?: string | null; url?: string | null };
  /**
   * Every server the pack runs on, this one included. With more than one the
   * announcement lists them all, each with its own location, so a cluster
   * launches in a single post.
   */
  members?: LaunchServer[];
  onAnnounced?: (result: AnnounceLaunchResult) => void;
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

/** The flag the bot shows for a location: AU, then EU, else US. */
export const flagForLocation = (location: string) => {
  const upper = location.toUpperCase();
  if (upper.includes('AU')) return '🇦🇺';
  if (upper.includes('EU')) return '🇪🇺';
  return '🇺🇸';
};

interface Row {
  address: string;
  included: boolean;
  location: string;
}

/**
 * Announcing a new pack — what /packembed does.
 *
 * Separate from provisioning and from creating the row, for the same reason
 * those are separate from each other: it talks to Discord, it can fail on its
 * own, and it is the one step of a launch that cannot be quietly redone
 * without players noticing. So it is explicit, last, and reachable again from
 * the pack's page if a launch stopped before it.
 */
export const AnnounceLaunchPanel = ({ address, env, defaults, members, onAnnounced }: AnnounceLaunchPanelProps) => {
  const [description, setDescription] = useState('');
  // This server first, then the rest of the cluster; each starts labelled
  // with its region, which is what the location usually is.
  const [rows, setRows] = useState<Row[]>(() => {
    const seen = new Set<string>();
    const ordered: LaunchServer[] = [];
    for (const m of [
      { server_address: address, region_name: defaults?.location ?? null },
      ...(members ?? []),
    ]) {
      const key = m.server_address.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      ordered.push(m);
    }
    return ordered.map((m) => ({ address: m.server_address, included: true, location: m.region_name ?? '' }));
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<AnnounceLaunchResult | null>(null);

  const several = rows.length > 1;
  const chosen = useMemo(() => rows.filter((r) => r.included), [rows]);
  const incomplete = chosen.filter((r) => r.location.trim() === '');
  const canSubmit = description.trim() !== '' && chosen.length > 0 && incomplete.length === 0 && !submitting;

  const setRow = (index: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const handleAnnounce = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await modpackConfigsApiService.announceLaunch(
        address,
        several
          ? {
            description: description.trim(),
            servers: chosen.map((r) => ({ server_address: r.address, location: r.location.trim() })),
          }
          : { description: description.trim(), location: chosen[0].location.trim() },
        env,
      );
      setDone(result);
      onAnnounced?.(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to announce');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="subcard p-4">
      <SectionHeader
        title="Announce the launch"
        icon={Megaphone}
        description={several
          ? 'Posts the launch embed to the pack-announcements channel: name, download link, description, version, and every server you tick with its location.'
          : 'Posts the launch embed to the pack-announcements channel: name, download link, description, server address, version and location.'}
      />

      {done ? (
        <div className="alert alert-success" role="status">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>Announced{done.message_id ? ` (message ${done.message_id})` : ''}.</span>
        </div>
      ) : (
        <div className="space-y-3">
          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="launch-description" className={LABEL_CLASS}>Description</label>
            <textarea
              id="launch-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What the pack is, who it's for, what makes it worth playing…"
              className="glass-input w-full min-h-[6rem]"
              maxLength={4000}
              required
            />
          </div>

          {several ? (
            <div>
              <p className={LABEL_CLASS}>Servers in the announcement</p>
              <div className="subcard divide-y divide-glass-200/10">
                {rows.map((row, index) => (
                  <div key={row.address} className="flex flex-wrap items-center gap-3 px-3 py-2">
                    <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer">
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        checked={row.included}
                        onChange={(e) => setRow(index, { included: e.target.checked })}
                      />
                      <span className="font-mono text-sm text-white truncate">{row.address}</span>
                      {row.address.toLowerCase() === address.toLowerCase() && (
                        <span className="badge badge-neutral shrink-0">this pack</span>
                      )}
                    </label>
                    <span className="inline-flex items-center gap-2">
                      <span className="text-base leading-none" aria-hidden>{row.location.trim() ? flagForLocation(row.location) : ''}</span>
                      <input
                        type="text"
                        value={row.location}
                        onChange={(e) => setRow(index, { location: e.target.value })}
                        placeholder="AU, EU, US…"
                        aria-label={`Location for ${row.address}`}
                        className={cn('glass-input w-28', row.included && !row.location.trim() && 'border-amber-500/40')}
                        maxLength={80}
                        disabled={!row.included}
                      />
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-glass-500 mt-1">
                {chosen.length === 0
                  ? 'Tick at least one server.'
                  : incomplete.length > 0
                    ? `Give ${incomplete.length === 1 ? incomplete[0].address : `${incomplete.length} servers`} a location.`
                    : `${chosen.length} server${chosen.length === 1 ? '' : 's'} listed, each with its flag. Untick any that are not ready.`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label htmlFor="launch-location" className={LABEL_CLASS}>Location</label>
                <input
                  id="launch-location"
                  type="text"
                  value={rows[0]?.location ?? ''}
                  onChange={(e) => setRow(0, { location: e.target.value })}
                  placeholder="AU, EU, US…"
                  className="glass-input w-full"
                  maxLength={80}
                  required
                />
                <p className="text-xs text-glass-500 mt-1">Shown with its flag, as /packembed does.</p>
              </div>
              <div className="text-xs text-glass-400 self-end pb-1">
                {defaults?.url
                  ? <>Links to <span className="break-all">{defaults.url}</span></>
                  : <span className="text-amber-300">No download URL is set on this config, so the announcement cannot link to one.</span>}
                {defaults?.version && <> · version <span className="font-mono">{defaults.version}</span></>}
              </div>
            </div>
          )}

          {several && (
            <div className="text-xs text-glass-400">
              {defaults?.url
                ? <>Links to <span className="break-all">{defaults.url}</span></>
                : <span className="text-amber-300">No download URL is set on this config, so the announcement cannot link to one.</span>}
              {defaults?.version && <> · version <span className="font-mono">{defaults.version}</span></>}
            </div>
          )}

          <button type="button" onClick={handleAnnounce} disabled={!canSubmit} className="btn-primary">
            {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
            Announce launch
          </button>
        </div>
      )}
    </div>
  );
};
